/**
 * Layer descriptions — one place for "what is this and why does it matter"
 * copy that backs the ⓘ button next to each toggle in the Layers menu
 * (2D and 3D). Add new keys here when you add a new toggle.
 *
 * Keep titles short (≤ 4 words) and descriptions honest about limitations.
 * Users in an emergency need to know what each overlay can and can't tell
 * them — fluffy marketing copy makes the tool feel less trustworthy.
 */

export interface LayerInfo {
  /** Short title shown in the modal header. */
  title: string;
  /** 1–3 sentence plain-English explanation. Markdown not supported. */
  description: string;
  /** Why someone should care; one sentence. */
  why?: string;
  /** Caveats / limitations users should be aware of. */
  caveat?: string;
  /** Data source attribution (where the underlying numbers come from). */
  source?: string;
  /** Update cadence ("every 10 min", "hourly", "as published"). */
  updateFrequency?: string;
}

export const LAYER_INFO: Record<string, LayerInfo> = {
  fire_boundaries: {
    title: 'Fire boundaries',
    description: 'Polygon shapes showing the actual perimeter of larger fires, not just the centre point.',
    why: 'Lets you see how big a fire is and which side it\'s closest to you.',
    caveat: 'Not every fire has a published boundary — small or new fires may only show as a marker.',
    source: 'NSW RFS, VIC OSOM',
    updateFrequency: 'Every 10 minutes',
  },
  warning_areas: {
    title: 'Warning areas',
    description: 'Polygon zones that fire services have placed under an active warning (Advice / Watch & Act / Emergency).',
    why: 'Identifies the geographic scope of a warning, so you can see if your location is inside it.',
    source: 'State fire services',
    updateFrequency: 'Every 10 minutes',
  },
  hotspots: {
    title: 'Satellite hotspots',
    description: 'Thermal anomalies detected by satellites — places that look hot from space.',
    why: 'Often picks up new fires before they\'re officially reported.',
    caveat: 'Includes false positives: sun-heated rocks, industrial sites, gas flares. Cross-reference with official fire incidents.',
    source: 'Geoscience Australia (Himawari, MODIS, VIIRS) and NASA FIRMS',
    updateFrequency: 'Every 10 minutes',
  },
  road_closures: {
    title: 'Road closures',
    description: 'Live road closure points from state transport agencies.',
    why: 'Shows which routes are blocked — useful for planning evacuation or detours near a fire.',
    caveat: 'Includes non-fire closures (accidents, flooding, roadworks). NSW + VIC coverage; other states limited.',
    source: 'TfNSW Live Traffic, VicRoads',
    updateFrequency: 'Every 10 minutes',
  },
  power_outages: {
    title: 'Power outages',
    description: 'Reported electricity outages from network providers.',
    why: 'Outages near a fire can indicate damaged infrastructure or precautionary network shutdowns.',
    caveat: 'Coverage varies by state and provider. Not every outage relates to fire activity.',
    source: 'Network provider feeds',
    updateFrequency: 'Every 10 minutes',
  },
  wind: {
    title: 'Wind',
    description: 'Animated particles showing wind direction and speed at 10 metres above ground.',
    why: 'Wind direction predicts where a fire will spread next. Watch for shifts ahead of a front change.',
    caveat: 'Forecast model — actual ground-level wind near a fire can differ due to terrain and pyroconvection.',
    source: 'NOAA GFS via Open-Meteo',
    updateFrequency: 'Hourly',
  },
  aircraft: {
    title: 'Aircraft tracking',
    description: 'Live water-bombers, helicopters and observation aircraft tracked via ADS-B.',
    why: 'Aerial firefighting activity is one of the most reliable signals that a fire is being actively fought.',
    caveat: 'Not every firefighting aircraft transmits ADS-B. Supporter-only feature in some plans.',
    source: 'ADS-B Exchange',
    updateFrequency: 'Every 30 seconds',
  },
  smoke: {
    title: 'Smoke forecast',
    description: 'Forecast smoke plume coverage from active fires.',
    why: 'Indicates where smoke is likely to drift, which affects respiratory health risk.',
    caveat: 'Forecast — actual smoke visibility on the ground depends on wind, humidity and inversion layers.',
    source: 'NOAA HRRR-Smoke / Copernicus',
    updateFrequency: 'Hourly',
  },
  air_quality: {
    title: 'Air quality',
    description: 'Current Air Quality Index (AQI) — colour-graded from green (good) to maroon (hazardous).',
    why: 'Lets you check if outdoor air is safe for at-risk people (asthma, elderly, kids) during smoke events.',
    source: 'Open-Meteo Air Quality (combines BoM, EPA, satellite)',
    updateFrequency: 'Hourly',
  },
  city_temperatures: {
    title: 'City temperatures',
    description: 'Current temperature readings at major Australian cities and towns.',
    why: 'Hot, dry conditions correlate with elevated fire risk. Quick check vs. forecast highs.',
    source: 'BoM via Open-Meteo',
    updateFrequency: 'Hourly',
  },
  photos: {
    title: 'Community photos',
    description: 'Photos uploaded by users from on-the-ground at fire incidents.',
    why: 'Eyewitness photos add ground-truth context the official reports can\'t — smoke colour, flame height, damage.',
    caveat: 'Moderated before going live. Photos are dated; check the timestamp before relying on them for current conditions.',
  },
  thunderstorm_warnings: {
    title: 'Thunderstorm warnings',
    description: 'Active severe thunderstorm warning polygons from BoM.',
    why: 'Thunderstorms during fire weather can ignite new fires via lightning, plus pose their own wind/hail risk.',
    source: 'Bureau of Meteorology',
    updateFrequency: 'As issued',
  },
  dry_lightning_risk: {
    title: 'Dry lightning risk',
    description: 'Forecast risk grid of lightning ignitions in low-rainfall areas.',
    why: 'Dry lightning is one of the leading causes of remote bushfires. Areas in red are at elevated start-risk over the next 24h.',
    caveat: 'Forecast, not real-time strikes. Doesn\'t mean lightning has occurred — only that conditions favour it.',
    source: 'NOAA GFS CAPE-based model via Open-Meteo',
    updateFrequency: 'Every 30 minutes',
  },
  planned_burns: {
    title: 'Planned burns',
    description: 'Scheduled hazard-reduction and prescribed burns. Markers have a dashed border to distinguish them from wildfires.',
    why: 'Shown by default so you\'re aware of all fire activity near you. Toggle off to focus only on unplanned wildfires.',
    caveat: 'Detection coverage varies: NSW / VIC / QLD / SA are reliable; WA is best-effort; TAS / ACT rarely publish planned burns.',
    source: 'State fire services',
    updateFrequency: 'Every 10 minutes',
  },

  // 3D-specific layers
  town_labels: {
    title: 'Town names',
    description: 'Labels for Australian towns and cities overlaid on the 3D globe.',
    why: 'Helps you orient yourself when zoomed in — the 3D terrain alone has no place names baked in.',
    source: 'Geonames',
  },
  ember_attack: {
    title: 'Ember attack zones',
    description: 'Modelled ember-spread zones radiating downwind from active fires, sized by fire intensity and wind conditions.',
    why: 'Embers can ignite spot fires hundreds of metres beyond the main flame front — this layer shows the at-risk corridor.',
    caveat: 'Predictive model based on current wind and fire size, not real-time ember tracking. Visual estimate only — always follow official advice.',
    source: 'Calculated locally from fire intensity + wind data',
  },
};
